import { useEffect, useState } from 'react';
import { Truck, AlertCircle } from 'lucide-react';
import { notify } from '../../components/Notify';
import { buscarEnderecoPorCepApi, calcularFreteApi } from '../../api';
import { onlyDigits } from '../../validators';

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const moeda = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const maskCep = (v) => { const d = onlyDigits(v).slice(0, 8); return d.length > 5 ? `${d.slice(0,5)}-${d.slice(5)}` : d; };
const mapOpcoes = (data) => (data?.opcoes || []).map((o) => ({ ...o, valor: Number(o.valor) }));
const maisBarata = (ops) => ops.reduce((a, b) => (b.valor < a.valor ? b : a), ops[0]).codigo;

// Enquanto os Correios não entram, o frete é FIXO: as modalidades e preços já
// vêm cadastrados (Configurações da Loja → Frete) e são carregados ao abrir, sem
// "calcular". O CEP serve só pra ENDEREÇO — ao digitar, o endereço é preenchido
// automático (ViaCEP). Se um dia virar modo Correios, o CEP recalcula os preços.
export default function FreteCalculator({ produtoValor, onConfirmar }) {
    const [end, setEnd] = useState({ cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' });
    const [opcoes, setOpcoes] = useState([]);
    const [selecionada, setSelecionada] = useState(null);
    const [modo, setModo] = useState('fixo');
    const [carregando, setCarregando] = useState(true);
    const [buscandoCep, setBuscandoCep] = useState(false);

    const set = (campo) => (e) => setEnd((p) => ({ ...p, [campo]: e.target.value }));

    // Ao montar: traz as modalidades. Em modo FIXO vêm prontas (CEP vazio basta).
    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const r = await calcularFreteApi({ cepDestino: '' });
                if (!vivo) return;
                const ops = mapOpcoes(r.data);
                setModo(r.data?.modo || 'fixo');
                setOpcoes(ops);
                if (ops.length) setSelecionada(maisBarata(ops));
            } catch {
                // Modo Correios (precisa do CEP) ou sem modalidade: fica pra busca do CEP.
                if (vivo) setModo('correios');
            } finally {
                if (vivo) setCarregando(false);
            }
        })();
        return () => { vivo = false; };
    }, []);

    // CEP: ao completar 8 dígitos, busca o endereço (ViaCEP). No modo Correios,
    // também recalcula os preços com base no CEP.
    const resolverCep = async (cep) => {
        setBuscandoCep(true);
        try {
            const v = await buscarEnderecoPorCepApi({ cep });
            if (v) {
                setEnd((p) => ({
                    ...p,
                    logradouro: v.logradouro || p.logradouro,
                    bairro: v.bairro || p.bairro,
                    cidade: v.localidade || p.cidade,
                    uf: v.uf || p.uf,
                }));
            }
            if (modo === 'correios') {
                const ops = mapOpcoes((await calcularFreteApi({ cepDestino: cep })).data);
                setOpcoes(ops);
                if (ops.length) setSelecionada(maisBarata(ops));
            }
        } catch {
            notify('Não foi possível buscar o endereço desse CEP.');
        } finally {
            setBuscandoCep(false);
        }
    };

    const aoMudarCep = (valor) => {
        const masked = maskCep(valor);
        setEnd((p) => ({ ...p, cep: masked }));
        if (onlyDigits(masked).length === 8) resolverCep(onlyDigits(masked));
    };

    // Campos OBRIGATÓRIOS da entrega física (complemento é o único opcional).
    const cepOk = onlyDigits(end.cep).length === 8;
    const opSelecionada = opcoes.find((o) => o.codigo === selecionada);
    const faltando = [];
    if (!cepOk) faltando.push('CEP');
    if (!end.logradouro.trim()) faltando.push('logradouro');
    if (!end.numero.trim()) faltando.push('número');
    if (!end.bairro.trim()) faltando.push('bairro');
    if (!end.cidade.trim()) faltando.push('cidade');
    if (!end.uf.trim()) faltando.push('UF');
    if (opcoes.length && !opSelecionada) faltando.push('modalidade de entrega');
    const completo = opcoes.length > 0 && opSelecionada && faltando.length === 0;

    // Não há mais botão "Confirmar": o endereço é emitido automaticamente pro pai
    // assim que fica completo (o "Continuar para pagamento" do checkout confirma).
    // Enquanto faltar algo, emite null e o "Continuar" fica travado.
    useEffect(() => {
        if (completo) {
            onConfirmar({
                cepDestino: onlyDigits(end.cep),
                freteValor: opSelecionada.valor,
                valorTotal: Number(produtoValor) + opSelecionada.valor,
                codigoServico: opSelecionada.codigo,
                modalidadeNome: opSelecionada.nome,
                endereco: { ...end, cep: onlyDigits(end.cep) },
            });
        } else {
            onConfirmar(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [end, opcoes, selecionada, produtoValor]);

    return (
        <div className="ueb-frete">
            <h3 className="ueb-co-h3"><Truck size={17} /> Endereço de entrega</h3>

            <div className="ueb-frete-cep">
                <input className="pinput" placeholder="CEP" inputMode="numeric" value={end.cep}
                    onChange={(e) => aoMudarCep(e.target.value)} />
                {buscandoCep && <span className="ueb-frete-cep-load"><span className="pspin" /> Buscando endereço…</span>}
            </div>

            <div className="ueb-frete-grid">
                <input className="pinput" placeholder="Logradouro" value={end.logradouro} onChange={set('logradouro')} />
                <input className="pinput" placeholder="Número" value={end.numero} onChange={set('numero')} />
                <input className="pinput" placeholder="Complemento (opcional)" value={end.complemento} onChange={set('complemento')} />
                <input className="pinput" placeholder="Bairro" value={end.bairro} onChange={set('bairro')} />
                <input className="pinput" placeholder="Cidade" value={end.cidade} onChange={set('cidade')} />
                <select className="pselect" value={end.uf} onChange={set('uf')}>
                    <option value="">UF</option>
                    {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
            </div>

            {carregando && (
                <p className="ueb-frete-faltando"><span className="pspin" /> Carregando modalidades de entrega…</p>
            )}
            {!carregando && opcoes.length === 0 && (
                <p className="ueb-frete-faltando"><AlertCircle size={14} />
                    {modo === 'correios'
                        ? ' Digite o CEP para ver as opções de entrega.'
                        : ' Nenhuma modalidade de entrega disponível no momento. Fale com a equipe.'}
                </p>
            )}

            {opcoes.length > 0 && (
                <div className="ueb-frete-opcoes">
                    <span className="ueb-co-label">Modalidade de entrega</span>
                    {opcoes.map((o) => (
                        <label key={o.codigo} className={`ueb-frete-opcao ${selecionada === o.codigo ? 'is-sel' : ''}`}>
                            <input type="radio" name="frete" checked={selecionada === o.codigo} onChange={() => setSelecionada(o.codigo)} />
                            <span className="ueb-frete-opcao-info">
                                <strong>{o.nome}</strong>
                                {o.prazoUtilDias ? <small className="pfaint">{o.prazoUtilDias} dias úteis</small> : null}
                            </span>
                            <span className="ueb-frete-opcao-valor">{moeda(o.valor)}</span>
                        </label>
                    ))}
                </div>
            )}

            {opcoes.length > 0 && faltando.length > 0 && (
                <p className="ueb-frete-faltando"><AlertCircle size={14} /> Falta preencher: {faltando.join(', ')}.</p>
            )}
        </div>
    );
}
